import { useState } from "react";

type AuthMode = "login" | "signup";
type ContactMethod = "email" | "phone";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const [mode, setMode] = useState<AuthMode>("login");
  const [method, setMethod] = useState<ContactMethod>("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const validate = () => {
    const errs: Record<string, string> = {};
    if (mode === "signup" && !name.trim()) errs.name = "Full name is required";
    if (method === "email") {
      if (!email.trim()) errs.contact = "Email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.contact = "Enter a valid email address";
    } else {
      if (!phone.trim()) errs.contact = "Phone number is required";
      else if (!/^\+?[\d\s\-()]{7,}$/.test(phone)) errs.contact = "Enter a valid phone number";
    }
    if (!password) errs.password = "Password is required";
    else if (password.length < 6) errs.password = "Password must be at least 6 characters";
    if (mode === "signup" && password !== confirmPassword) errs.confirmPassword = "Passwords do not match";
    return errs;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setSubmitted(true);
  };

  const handleClose = () => {
    setSubmitted(false);
    setEmail(""); setPhone(""); setPassword(""); setConfirmPassword(""); setName("");
    setErrors({});
    setMode("login");
    onClose();
  };

  const switchMode = (m: AuthMode) => {
    setMode(m);
    setErrors({});
    setPassword(""); setConfirmPassword("");
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose}></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl w-full max-w-md overflow-hidden">
        {/* Top accent */}
        <div className="h-1.5 bg-gradient-to-r from-amber-400 to-orange-500 w-full"></div>

        <div className="p-7">
          {/* Close */}
          <button
            onClick={handleClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 hover:bg-stone-200 text-stone-500 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>

          {!submitted ? (
            <>
              {/* Logo + Title */}
              <div className="text-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-amber-100 mx-auto mb-3">
                  <i className="ri-vip-crown-fill text-amber-500 text-2xl"></i>
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  {mode === "login" ? "Welcome Back" : "Create Your Account"}
                </h2>
                <p className="text-stone-500 text-sm mt-1">
                  {mode === "login" ? "Sign in to access your CrownBoost account" : "Join CrownBoost — it's free to get started"}
                </p>
              </div>

              {/* Mode toggle */}
              <div className="flex bg-stone-100 rounded-full p-1 mb-6">
                <button
                  onClick={() => switchMode("login")}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    mode === "login" ? "bg-white text-gray-900" : "text-stone-500 hover:text-stone-700"
                  }`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => switchMode("signup")}
                  className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    mode === "signup" ? "bg-white text-gray-900" : "text-stone-500 hover:text-stone-700"
                  }`}
                >
                  Sign Up
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Full name (signup only) */}
                {mode === "signup" && (
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Full Name</label>
                    <div className="relative">
                      <i className="ri-user-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm"></i>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your full name"
                        className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>
                )}

                {/* Contact method toggle */}
                <div>
                  <label className="block text-xs font-semibold text-stone-600 mb-1.5">Sign in with</label>
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => { setMethod("email"); setErrors({}); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        method === "email"
                          ? "border-amber-400 bg-amber-50 text-amber-700"
                          : "border-stone-200 text-stone-500 hover:border-stone-300"
                      }`}
                    >
                      <i className="ri-mail-line"></i> Email
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMethod("phone"); setErrors({}); }}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                        method === "phone"
                          ? "border-amber-400 bg-amber-50 text-amber-700"
                          : "border-stone-200 text-stone-500 hover:border-stone-300"
                      }`}
                    >
                      <i className="ri-phone-line"></i> Phone
                    </button>
                  </div>

                  {method === "email" ? (
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm"></i>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                  ) : (
                    <div className="relative">
                      <i className="ri-phone-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm"></i>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+1 (555) 000-0000"
                        className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                  )}
                  {errors.contact && <p className="text-red-500 text-xs mt-1">{errors.contact}</p>}
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-stone-600">Password</label>
                    {mode === "login" && (
                      <button type="button" className="text-xs text-amber-600 hover:underline cursor-pointer">
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm"></i>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="w-full pl-9 pr-10 py-2.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-amber-400 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 cursor-pointer w-5 h-5 flex items-center justify-center"
                    >
                      <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                </div>

                {/* Confirm password (signup only) */}
                {mode === "signup" && (
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Confirm Password</label>
                    <div className="relative">
                      <i className="ri-lock-2-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm"></i>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter your password"
                        className="w-full pl-9 pr-4 py-2.5 border border-stone-200 rounded-lg text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm mt-2"
                >
                  {mode === "login" ? "Sign In" : "Create Account"}
                </button>

                {/* Terms (signup) */}
                {mode === "signup" && (
                  <p className="text-xs text-stone-400 text-center">
                    By signing up, you agree to our{" "}
                    <span className="text-amber-600 cursor-pointer hover:underline">Terms of Service</span>{" "}
                    and{" "}
                    <span className="text-amber-600 cursor-pointer hover:underline">Privacy Policy</span>
                  </p>
                )}
              </form>

              {/* Divider */}
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-stone-100"></div>
                <span className="text-xs text-stone-400">or continue with</span>
                <div className="flex-1 h-px bg-stone-100"></div>
              </div>

              {/* Social login */}
              <div className="flex gap-3">
                <button className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-stone-200 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-google-fill text-red-500"></i> Google
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-stone-200 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-facebook-fill text-stone-700"></i> Facebook
                </button>
                <button className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-stone-200 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-apple-fill text-stone-800"></i> Apple
                </button>
              </div>
            </>
          ) : (
            /* Success screen */
            <div className="text-center py-6">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {mode === "login" ? "Welcome Back!" : "Account Created!"}
              </h2>
              <p className="text-stone-500 text-sm mb-6">
                {mode === "login"
                  ? "You have successfully signed in to your CrownBoost account."
                  : "Your CrownBoost account is ready. Start exploring jobs, ads, and buyer requests!"}
              </p>
              <button
                onClick={handleClose}
                className="px-8 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                Get Started
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
